package com.karyadina.ui.screens.flashcards.components

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.fadeIn
import androidx.compose.animation.slideInVertically
import androidx.compose.animation.core.tween
import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.EaseInOutCubic
import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.material.icons.outlined.Delete
import androidx.compose.material3.*
import androidx.compose.material3.ripple
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha

import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.karyadina.model.Flashcard
import com.karyadina.model.MasteryLevel
import com.karyadina.model.ScanMode
import com.karyadina.ui.components.CameraScanBottomSheet
import com.karyadina.ui.components.ImageViewerDialog
import com.karyadina.ui.components.ImageThumbnail
import com.karyadina.ui.theme.StudyColors
import com.karyadina.ui.theme.FlashcardColors
import com.karyadina.ui.theme.FlashcardIcons
import com.karyadina.ui.theme.FlashcardAnimations
import com.karyadina.util.currentTimeMillis
import com.karyadina.util.rememberImagePickerLauncher
import kotlinx.coroutines.delay
import com.karyadina.ui.theme.ElevationProfile
import com.karyadina.ui.theme.ElevationUtils.rippleEffect
import kotlinx.datetime.Clock

@Composable
fun MasteryLevelChip(
    label: String,
    count: Int,
    color: Color,
    icon: androidx.compose.ui.graphics.vector.ImageVector
) {
    Column(
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(4.dp)
    ) {
        Surface(
            shape = CircleShape,
            color = color.copy(alpha = 0.2f),
            modifier = Modifier.size(40.dp)
        ) {
            Box(contentAlignment = Alignment.Center) {
                Icon(
                    icon,
                    contentDescription = null,
                    tint = color,
                    modifier = Modifier.size(20.dp)
                )
            }
        }
        Text(
            count.toString(),
            style = MaterialTheme.typography.titleSmall,
            fontWeight = FontWeight.Bold
        )
        Text(
            label,
            style = MaterialTheme.typography.labelSmall,
            color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.6f)
        )
    }
}

@Composable
fun FlashcardItem(
    card: Flashcard,
    subjectColor: String,
    onToggleFavorite: () -> Unit,
    onDelete: () -> Unit,
    onReviewThisCard: (() -> Unit)? = null,
    itemIndex: Int = 0
) {
    var expanded by remember { mutableStateOf(false) }
    var showDeleteDialog by remember { mutableStateOf(false) }
    var isVisible by remember { mutableStateOf(false) }
    var showImageViewer by remember { mutableStateOf(false) }
    var selectedImageIndex by remember { mutableStateOf(0) }

    // Staggered animation entry
    LaunchedEffect(itemIndex) {
        delay((itemIndex * 50L).coerceIn(0L, 300L))
        isVisible = true
    }

    val masteryLevel = try {
        MasteryLevel.valueOf(card.masteryLevel)
    } catch (e: Exception) {
        MasteryLevel.NEW
    }

    val masteryColor = FlashcardColors.getMasteryColor(card.masteryLevel)

    // Check if card is due for review
    val now = currentTimeMillis()
    val isDue = card.nextReviewDate?.let { it <= now } ?: false
    
    // Smooth pulse animation for due cards (only non-mastered)
    val pulseAlpha by animateFloatAsState(
        targetValue = if (isDue && card.masteryLevel != "MASTERED") 0.6f else 1f,
        animationSpec = tween(
            durationMillis = 1500,
            easing = EaseInOutCubic
        ),
        label = "cardPulse"
    )

    AnimatedVisibility(
        visible = isVisible,
        enter = slideInVertically(
            initialOffsetY = { 30 }
        ) + fadeIn(),
    ) {
        ElevatedCard(
            onClick = { expanded = !expanded },
            modifier = Modifier
                .fillMaxWidth()
                .graphicsLayer {
                    alpha = if (isDue) pulseAlpha else 1f
                }
                .rippleEffect(bounded = true),
            elevation = CardDefaults.elevatedCardElevation(
                defaultElevation = ElevationProfile.standardElevation
            )
        ) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(12.dp)
            ) {
                // Header - Compact, fluid layout
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    // Mastery badge using AssistChip style
                    AssistChip(
                        onClick = { },
                        label = {
                            Text(
                                masteryLevel.displayName,
                                style = MaterialTheme.typography.labelSmall,
                                fontWeight = FontWeight.Bold
                            )
                        },
                        shape = RoundedCornerShape(8.dp),
                        colors = AssistChipDefaults.assistChipColors(
                            containerColor = masteryColor.copy(alpha = 0.12f),
                            labelColor = masteryColor
                        ),
                        border = BorderStroke(1.dp, masteryColor.copy(alpha = 0.5f)),
                        modifier = Modifier.height(28.dp)
                    )

                    Row(modifier = Modifier.height(28.dp)) {
                        // Review button (only for due, non-mastered cards)
                        if (isDue && card.masteryLevel != "MASTERED" && onReviewThisCard != null) {
                            IconButton(
                                onClick = onReviewThisCard,
                                modifier = Modifier.size(28.dp)
                            ) {
                                Icon(
                                    Icons.Filled.PlayArrow,
                                    "Review",
                                    tint = MaterialTheme.colorScheme.primary,
                                    modifier = Modifier.size(18.dp)
                                )
                            }
                        }
                        IconButton(
                            onClick = onToggleFavorite,
                            modifier = Modifier.size(28.dp)
                        ) {
                            Icon(
                                if (card.isFavorite) FlashcardIcons.StarFilled else FlashcardIcons.StarOutlined,
                                "Favorite",
                                tint = if (card.isFavorite) FlashcardColors.getFavoriteColor() else MaterialTheme.colorScheme.onSurface.copy(
                                    alpha = 0.5f
                                ),
                                modifier = Modifier.size(18.dp)
                            )
                        }
                        IconButton(
                            onClick = { showDeleteDialog = true },
                            modifier = Modifier.size(28.dp)
                        ) {
                            Icon(
                                Icons.Outlined.Delete,
                                "Delete",
                                tint = MaterialTheme.colorScheme.error,
                                modifier = Modifier.size(18.dp)
                            )
                        }
                    }
                }

                Spacer(modifier = Modifier.height(4.dp))

                // Next Review Date (if scheduled) - Compact display
                if (card.masteryLevel != "MASTERED") {
                    card.nextReviewDate?.let { nextReview ->
                        val now = currentTimeMillis()
                        val isNowDue = nextReview <= now
                        val timeUntilReview =
                            ((nextReview - now) / (1000 * 60 * 60 * 24)).toInt() // days

                        Row(
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(6.dp),
                            modifier = Modifier.padding(vertical = 2.dp)
                        ) {
                            Icon(
                                if (isNowDue) Icons.Filled.NotificationImportant else Icons.Filled.Schedule,
                                contentDescription = null,
                                modifier = Modifier.size(14.dp),
                                tint = if (isNowDue) MaterialTheme.colorScheme.error else if (timeUntilReview == 0) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.tertiary
                            )
                            Text(
                                if (isNowDue) {
                                    "Review now!"
                                } else {
                                    when {
                                        timeUntilReview == 0 -> "Review today"
                                        timeUntilReview == 1 -> "Review tomorrow"
                                        timeUntilReview < 7 -> "Review in $timeUntilReview days"
                                        timeUntilReview < 30 -> "Review in ${timeUntilReview / 7} weeks"
                                        else -> "Review in ${timeUntilReview / 30} months"
                                    }
                                },
                                style = MaterialTheme.typography.labelSmall,
                                color = if (isNowDue) MaterialTheme.colorScheme.error else if (timeUntilReview == 0) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.tertiary,
                                fontWeight = if (isNowDue) FontWeight.Bold else FontWeight.Medium
                            )
                        }

                        Spacer(modifier = Modifier.height(4.dp))
                    } ?: run {
                        // NEW cards - show with primary color
                        if (card.masteryLevel == "NEW") {
                            Row(
                                verticalAlignment = Alignment.CenterVertically,
                                horizontalArrangement = Arrangement.spacedBy(6.dp),
                                modifier = Modifier.padding(vertical = 2.dp)
                            ) {
                                Icon(
                                    Icons.Filled.Info,
                                    contentDescription = null,
                                    modifier = Modifier.size(14.dp),
                                    tint = MaterialTheme.colorScheme.primary
                                )
                                Text(
                                    "Ready to learn",
                                    style = MaterialTheme.typography.labelSmall,
                                    color = MaterialTheme.colorScheme.primary,
                                    fontWeight = FontWeight.Medium
                                )
                            }
                            Spacer(modifier = Modifier.height(4.dp))
                        }
                    }
                }

                // Tags display - Using AssistChips for cleaner look
                if (card.tags.isNotEmpty()) {
                    Spacer(modifier = Modifier.height(2.dp))
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .horizontalScroll(rememberScrollState()),
                        horizontalArrangement = Arrangement.spacedBy(4.dp)
                    ) {
                        card.tags.forEach { tag ->
                            val color = FlashcardColors.getTagColor(tag)
                            val icon = FlashcardIcons.getTagIcon(tag)

                            AssistChip(
                                onClick = { },
                                label = {
                                    Text(
                                        tag.truncateToWords(15),
                                        style = MaterialTheme.typography.labelSmall,
                                        fontWeight = FontWeight.SemiBold,
                                        color = Color.White
                                    )
                                },
                                leadingIcon = {
                                    Text(icon, style = MaterialTheme.typography.labelSmall, color = Color.White)
                                },
                                shape = RoundedCornerShape(6.dp),
                                colors = AssistChipDefaults.assistChipColors(
                                    containerColor = color.copy(alpha = 0.8f),
                                    labelColor = Color.White
                                ),
                                border = BorderStroke(1.dp, color.copy(alpha = 0.6f)),
                                modifier = Modifier.height(24.dp)
                            )
                        }
                    }
                }

                Spacer(modifier = Modifier.height(4.dp))

                // Question (Collapsed/Expandable)
                Row(
                    modifier = Modifier
                        .fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text(
                        card.question,
                        style = MaterialTheme.typography.bodyLarge,
                        fontWeight = FontWeight.SemiBold,
                        modifier = Modifier.weight(1f),
                        maxLines = if (expanded) Int.MAX_VALUE else 2
                    )
                    
                    Icon(
                        if (expanded) Icons.Filled.KeyboardArrowUp else Icons.Filled.KeyboardArrowDown,
                        contentDescription = "Expand/Collapse",
                        tint = MaterialTheme.colorScheme.primary,
                        modifier = Modifier.size(20.dp)
                    )
                }

                // Answer (Expandable) - Fluid spacing
                AnimatedVisibility(visible = expanded) {
                    Column {
                        Spacer(modifier = Modifier.height(8.dp))
                        HorizontalDivider(modifier = Modifier.alpha(0.3f))
                        Spacer(modifier = Modifier.height(8.dp))

                        Text(
                            card.answer.truncateToWords(100),
                            style = MaterialTheme.typography.bodyMedium,
                            color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.85f)
                        )

                        if (card.hint != null) {
                            Spacer(modifier = Modifier.height(6.dp))
                            Surface(
                                shape = RoundedCornerShape(6.dp),
                                color = MaterialTheme.colorScheme.secondaryContainer.copy(alpha = 0.15f)
                            ) {
                                Text(
                                    card.hint!!.truncateToWords(80),
                                    style = MaterialTheme.typography.bodySmall,
                                    color = MaterialTheme.colorScheme.onSecondaryContainer.copy(alpha = 0.9f),
                                    modifier = Modifier.padding(8.dp)
                                )
                            }
                        }

                        // Display formula if present
                        if (card.hasFormula && card.formulaLatex != null) {
                            Spacer(modifier = Modifier.height(8.dp))
                            com.karyadina.ui.components.FormulaDisplayBox(
                                formulaImagePath = card.formulaImagePath,
                                formulaLatex = card.formulaLatex,
                                formulaDescription = card.formulaDescription,
                                formulaVariables = card.formulaVariables,
                                onEdit = { /* TODO: Add edit functionality */ }
                            )
                        }

                        // Display attached images
                        val displayImages = if (card.imagePaths.isNotEmpty()) {
                            card.imagePaths
                        } else if (card.imagePath != null) {
                            listOf(card.imagePath)
                        } else {
                            emptyList()
                        }

                        if (displayImages.isNotEmpty()) {
                            Spacer(modifier = Modifier.height(8.dp))
                            Text(
                                "Images (${displayImages.size})",
                                style = MaterialTheme.typography.labelSmall,
                                fontWeight = FontWeight.SemiBold,
                                color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.6f)
                            )
                            Spacer(modifier = Modifier.height(6.dp))
                            Row(
                                horizontalArrangement = Arrangement.spacedBy(6.dp),
                                modifier = Modifier.horizontalScroll(rememberScrollState())
                            ) {
                                displayImages.forEachIndexed { index, imagePath ->
                                    ImageThumbnail(
                                        imagePath = imagePath,
                                        modifier = Modifier.size(80.dp),
                                        onView = {
                                            selectedImageIndex = index
                                            showImageViewer = true
                                        },
                                        showRemoveButton = false,
                                        onRemove = {}
                                    )
                                }
                            }
                        }
                    }
                }
            }
        }

        // Image Viewer Dialog
        if (showImageViewer) {
            val displayImages = if (card.imagePaths.isNotEmpty()) {
                card.imagePaths
            } else if (card.imagePath != null) {
                listOf(card.imagePath)
            } else {
                emptyList()
            }
            
            if (displayImages.isNotEmpty()) {
                ImageViewerDialog(
                    imagePaths = displayImages,
                    initialIndex = selectedImageIndex,
                    onDismiss = { showImageViewer = false }
                )
            }
        }
        
        if (showDeleteDialog) {
            val isFavorite = card.isFavorite
            val isSystemDark = isSystemInDarkTheme()
            val warningColor =
                if (isSystemDark) StudyColors.WarningDark else StudyColors.WarningLight
            val warningBg = FlashcardColors.getWarningBackgroundColor()

            AlertDialog(
                onDismissRequest = { showDeleteDialog = false },
                title = {
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        if (isFavorite) {
                            Icon(
                                FlashcardIcons.StarFilled,
                                contentDescription = null,
                                tint = FlashcardColors.getFavoriteColor(),
                                modifier = Modifier.size(24.dp)
                            )
                        }
                        Text(
                            if (isFavorite) "Delete Favorite Flashcard?" else "Delete Flashcard?",
                            color = if (isFavorite) warningColor else MaterialTheme.colorScheme.onSurface
                        )
                    }
                },
                text = {
                    Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                        if (isFavorite) {
                            Surface(
                                shape = RoundedCornerShape(8.dp),
                                color = warningBg,
                                modifier = Modifier.fillMaxWidth(),
                                border = BorderStroke(1.5.dp, warningColor)
                            ) {
                                Row(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .padding(12.dp),
                                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    Icon(
                                        Icons.Filled.Warning, contentDescription = null,
                                        tint = warningColor, modifier = Modifier.size(20.dp)
                                    )
                                    Column {
                                        Text(
                                            "This is a favorite card",
                                            style = MaterialTheme.typography.labelSmall,
                                            color = warningColor,
                                            fontWeight = FontWeight.Bold
                                        )
                                        Text(
                                            "Starred cards are important to you",
                                            style = MaterialTheme.typography.bodySmall,
                                            color = warningColor.copy(alpha = 0.8f)
                                        )
                                    }
                                }
                            }
                        }
                        Text("This action cannot be undone.")
                    }
                },
                confirmButton = {
                    TextButton(
                        onClick = {
                            onDelete()
                            showDeleteDialog = false
                        },
                        colors = ButtonDefaults.textButtonColors(
                            contentColor = if (isFavorite) warningColor else MaterialTheme.colorScheme.error
                        )
                    ) {
                        Text(
                            if (isFavorite) "Delete Anyway" else "Delete",
                            fontWeight = if (isFavorite) FontWeight.Bold else FontWeight.Normal
                        )
                    }
                },
                dismissButton = {
                    TextButton(onClick = { showDeleteDialog = false }) {
                        Text("Cancel")
                    }
                }
            )
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun AddFlashcardDialog(
    onDismiss: () -> Unit,
    onAdd: (String, String, String?, List<String>, List<String>, Boolean, String?, String?, String?) -> Unit
) {
    var question by remember { mutableStateOf("") }
        var answer by remember { mutableStateOf("") }
        var hint by remember { mutableStateOf("") }
        var tags by remember { mutableStateOf("") }
        var showScanSheet by remember { mutableStateOf(false) }
        var scannedImagePaths by remember { mutableStateOf<List<String>>(emptyList()) }
        var hasScannedContent by remember { mutableStateOf(false) }
        var showImageViewer by remember { mutableStateOf(false) }
        var selectedImageIndex by remember { mutableStateOf(0) }

        // Formula data
        var formulaLatex by remember { mutableStateOf<String?>(null) }
        var formulaDescription by remember { mutableStateOf<String?>(null) }
        var formulaType by remember { mutableStateOf<String?>(null) }
        var formulaConfidence by remember { mutableStateOf<Double?>(null) }

        val imagePickerLauncher = rememberImagePickerLauncher { uris ->
            // Add images to list (max 4 total)
            val spaceRemaining = 4 - scannedImagePaths.size
            if (spaceRemaining > 0) {
                scannedImagePaths = scannedImagePaths + uris.take(spaceRemaining)

                if (scannedImagePaths.isNotEmpty()) {
                    // Removed hint update

                }
            }
        }

        // Camera scan bottom sheet (full screen, replaces dialog)
        if (showScanSheet) {
            CameraScanBottomSheet(
                onDismiss = { showScanSheet = false },
                onScanComplete = { mode, extractedText, imagePath ->
                    // Add image to list (max 4 images)
                    if (imagePath != null && scannedImagePaths.size < 4) {
                        scannedImagePaths = scannedImagePaths + imagePath
                    }
                    hasScannedContent = imagePath != null || extractedText != null

                    extractedText?.let { text ->
                        when (mode) {
                            ScanMode.QUESTION -> question = text
                            ScanMode.ANSWER -> answer = text
                            ScanMode.IMAGE_ONLY -> {
                                // Image added to gallery
                                if (imagePath != null) {
                                    // Image added to gallery

                                }
                            }

                            ScanMode.FORMULA -> {
                                // Logic copied from original FlashcardsScreen.kt
                                // Parse formula JSON from extractedText
                                try {
                                    // Simple JSON parsing - handle escaped characters
                                    val latexMatch =
                                        """"latex":\s*"((?:[^"\\]|\\.)*)"""".toRegex().find(text)
                                    val descMatch =
                                        """"description":\s*"((?:[^"\\]|\\.)*)"""".toRegex()
                                            .find(text)
                                    val typeMatch = """"type":\s*"([^"]+)"""".toRegex().find(text)
                                    val confMatch =
                                        """"confidence":\s*([0-9.]+)""".toRegex().find(text)

                                    // Properly unescape the LaTeX string
                                    val rawLatex = latexMatch?.groupValues?.get(1)
                                    formulaLatex =
                                        rawLatex?.replace("\\\\", "\\")?.replace("\\\"", "\"")
                                    formulaDescription =
                                        descMatch?.groupValues?.get(1)?.replace("\\\\", "\\")
                                            ?.replace("\\\"", "\"")
                                    formulaType = typeMatch?.groupValues?.get(1)
                                    formulaConfidence =
                                        confMatch?.groupValues?.get(1)?.toDoubleOrNull()

                                    if (formulaLatex != null) {
                                        // hint = "📐 Formula recognized: ${formulaDescription ?: "Unknown"}"
                                    } else {
                                        // hint = "📐 Formula scanned - ${scannedImagePaths.size} image(s)"
                                    }
                                } catch (e: Exception) {
                                    // Parsing failed, treat as image-only
                                    // hint = "📐 Formula scanned - ${scannedImagePaths.size} image(s)"
                                    e.printStackTrace()
                                }
                            }
                        }
                    }
                    showScanSheet = false
                }
            )
            return // Don't render dialog when camera is active
        }

        AlertDialog(
            onDismissRequest = onDismiss,
            title = {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text("Add Flashcard")
                }
            },
            text = {
                Column(
                    verticalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    // Scan and Image Buttons
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        OutlinedButton(
                            onClick = { showScanSheet = true },
                            modifier = Modifier.weight(1f),
                            contentPadding = PaddingValues(horizontal = 8.dp)
                        ) {
                            Icon(Icons.Default.CameraAlt, null, Modifier.size(16.dp))
                            Spacer(Modifier.width(4.dp))
                            Text("Scan", style = MaterialTheme.typography.labelMedium)
                        }

                        OutlinedButton(
                            onClick = { imagePickerLauncher.launchPickImage(4 - scannedImagePaths.size) },
                            modifier = Modifier.weight(1f),
                            contentPadding = PaddingValues(horizontal = 8.dp),
                            enabled = scannedImagePaths.size < 4
                        ) {
                            Icon(Icons.Default.Image, null, Modifier.size(16.dp))
                            Spacer(Modifier.width(4.dp))
                            Text("Gallery", style = MaterialTheme.typography.labelMedium)
                        }
                    }

                    // Simplified UI for brevity, assume similar layout as original
                    // Hint banners would go here

                    OutlinedTextField(
                        value = question,
                        onValueChange = { question = it },
                        label = { Text("Question *") },
                        modifier = Modifier.fillMaxWidth(),
                        minLines = 2,
                        maxLines = 4
                    )

                    OutlinedTextField(
                        value = answer,
                        onValueChange = { answer = it },
                        label = { Text("Answer *") },
                        modifier = Modifier.fillMaxWidth(),
                        minLines = 2,
                        maxLines = 4
                    )

                    // Formula Display (simplified)
                    if (formulaLatex != null) {
                        Text("Formula: ${formulaDescription ?: ""}")
                    }

                    OutlinedTextField(
                        value = hint,
                        onValueChange = { hint = it },
                        label = { Text("Hint (Optional)") },
                        modifier = Modifier.fillMaxWidth(),
                        singleLine = true
                    )

                    OutlinedTextField(
                        value = tags,
                        onValueChange = { tags = it },
                        label = { Text("Tags (Optional)") },
                        modifier = Modifier.fillMaxWidth(),
                        singleLine = true
                    )

                    // Image list
                    if (scannedImagePaths.isNotEmpty()) {
                        Spacer(modifier = Modifier.height(8.dp))
                        Text(
                            "attached Images (${scannedImagePaths.size})",
                            style = MaterialTheme.typography.labelSmall
                        )
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .horizontalScroll(rememberScrollState()),
                            horizontalArrangement = Arrangement.spacedBy(8.dp)
                        ) {
                            scannedImagePaths.forEachIndexed { index, path ->
                                ImageThumbnail(
                                    imagePath = path,
                                    modifier = Modifier.size(80.dp),
                                    onView = {
                                        selectedImageIndex = index
                                        showImageViewer = true
                                    },
                                    onRemove = {
                                        scannedImagePaths =
                                            scannedImagePaths.filterIndexed { i, _ -> i != index }
                                    }
                                )
                            }
                        }
                    }
                }
            },
            confirmButton = {
                Button(
                    onClick = {
                        if (question.isNotBlank() && answer.isNotBlank()) {
                            val tagList =
                                tags.split(",").map { it.trim() }.filter { it.isNotBlank() }
                            onAdd(
                                question,
                                answer,
                                hint.ifBlank { null },
                                tagList,
                                scannedImagePaths,
                                hasScannedContent,
                                formulaLatex,
                                formulaDescription,
                                formulaType
                            )
                        }
                    },
                    enabled = question.isNotBlank() && answer.isNotBlank()
                ) {
                    Text("Add Card")
                }
            },
            dismissButton = {
                TextButton(onClick = onDismiss) {
                    Text("Cancel")
                }
            }
        )

        if (showImageViewer && scannedImagePaths.isNotEmpty()) {
            ImageViewerDialog(
                imagePaths = scannedImagePaths,
                initialIndex = selectedImageIndex,
                onDismiss = { showImageViewer = false }
            )
        }
    }
